import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { entityOverrideSet, selectEntityOverride } from '@features/auth/slice';
import type { EntityOverride } from '@features/auth/types';

interface UseEntityOverrideResult {
  entityOverride: EntityOverride | null;
  setEntityOverride: (next: EntityOverride | null) => void;
  clearEntityOverride: () => void;
  /** Convenience: merchantId iff the override is a concrete merchant. */
  scopedMerchantId: string | null;
  /** Convenience: vendorId iff the override is a concrete vendor. */
  scopedVendorId: string | null;
  /** True only for the "Continue as Admin" sentinel. */
  isAdminScope: boolean;
}

/**
 * Read/write access to the entity-override slice used by EntitySelector.
 * Pages wrapped by EntitySelector read `scopedMerchantId` /
 * `scopedVendorId` here rather than reaching into the auth user attributes
 * directly — this way the same hook works for SA "viewing as merchant"
 * and a plain merchant user.
 */
export function useEntityOverride(): UseEntityOverrideResult {
  const dispatch = useDispatch();
  const entityOverride = useSelector(selectEntityOverride);

  const setEntityOverride = useCallback(
    (next: EntityOverride | null) => {
      dispatch(entityOverrideSet(next));
    },
    [dispatch],
  );

  const clearEntityOverride = useCallback(() => {
    dispatch(entityOverrideSet(null));
  }, [dispatch]);

  return {
    entityOverride,
    setEntityOverride,
    clearEntityOverride,
    scopedMerchantId: entityOverride?.type === 'merchant' ? entityOverride.id : null,
    scopedVendorId: entityOverride?.type === 'vendor' ? entityOverride.id : null,
    isAdminScope: entityOverride?.type === 'admin',
  };
}
